import {
  ChevronLeft,
  ChevronRight,
  Download,
  ExternalLink,
  FileText,
  LoaderCircle,
} from "lucide-react";
import { useEffect, useState } from "react";

const demoLines = [
  "COMUNICACIÓN IMPORTANTE",
  "Referencia: actualización de condiciones del servicio",
  "Fecha de emisión: 8 de septiembre de 2026",
  "",
  "Le informamos que, a partir del 1 de octubre de 2026,",
  "se actualizará el valor mensual del servicio a $48.500.",
  "",
  "Si no está de acuerdo, podrá solicitar la baja por los",
  "canales de atención indicados al pie de esta comunicación.",
  "",
  "Canal de atención: 0800-000-0000",
];

function clamp(value, maximum) {
  return Math.max(1, Math.min(Number(value) || 1, maximum || 1));
}

export function DocumentPreview({
  api,
  document,
  page,
  onPageChange,
  label = "Documento original",
  demoContent = demoLines,
}) {
  const [url, setUrl] = useState("");
  const [state, setState] = useState({ loading: false, error: "" });
  const pageCount = document?.pageCount || 1;

  useEffect(() => {
    if (!document || document.demo) {
      setUrl("");
      setState({ loading: false, error: "" });
      return undefined;
    }
    let active = true;
    let objectUrl = "";
    setState({ loading: true, error: "" });
    api.getDocumentBlob(document.id)
      .then((file) => {
        if (!active) return;
        objectUrl = URL.createObjectURL(file);
        setUrl(objectUrl);
        setState({ loading: false, error: "" });
      })
      .catch((error) => {
        if (active) {
          setState({
            loading: false,
            error: error.message || "No pudimos abrir el PDF.",
          });
        }
      });
    return () => {
      active = false;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [api, document?.demo, document?.id]);

  const selectedPage = clamp(page, pageCount);
  const open = () => {
    if (!url) return;
    window.open(
      `${url}#page=${selectedPage}`,
      "_blank",
      "noopener,noreferrer",
    );
  };
  const download = () => {
    if (!url) return;
    const anchor = window.document.createElement("a");
    anchor.href = url;
    anchor.download = document.fileName || "documento.pdf";
    anchor.click();
  };

  return (
    <aside className="document-preview" aria-label="Documento original">
      <header className="document-preview__header">
        <div>
          <span>{label}</span>
          <strong title={document?.fileName}>{document?.fileName}</strong>
        </div>
        {!document?.demo && (
          <div className="document-preview__actions">
            <button type="button" onClick={download} disabled={!url} title="Descargar">
              <Download size={16} />
            </button>
            <button type="button" onClick={open} disabled={!url} title="Abrir aparte">
              <ExternalLink size={16} />
            </button>
          </div>
        )}
      </header>

      <div className="document-preview__body">
        {state.loading && (
          <div className="document-preview__message">
            <LoaderCircle className="is-spinning" size={24} />
            <span>Abriendo el documento…</span>
          </div>
        )}
        {state.error && (
          <div className="document-preview__message is-error">
            <FileText size={24} />
            <span>{state.error}</span>
          </div>
        )}
        {document?.demo && (
          <div className="demo-document-page">
            <div className="demo-document-mark">CIVITA · EJEMPLO</div>
            {demoContent.map((line, index) => line
              ? <p key={`${index}-${line}`}>{line}</p>
              : <br key={index} />)}
            <small>Página 1</small>
          </div>
        )}
        {url && !state.loading && (
          <iframe
            key={`${url}-${selectedPage}`}
            title={`Vista de ${document.fileName}`}
            src={`${url}#page=${selectedPage}&view=FitH&toolbar=0`}
          />
        )}
      </div>

      <footer className="document-preview__footer">
        <button
          type="button"
          onClick={() => onPageChange(clamp(selectedPage - 1, pageCount))}
          disabled={selectedPage <= 1}
          aria-label="Página anterior"
        >
          <ChevronLeft size={17} />
        </button>
        <span>Página {selectedPage}{pageCount > 1 ? ` de ${pageCount}` : ""}</span>
        <button
          type="button"
          onClick={() => onPageChange(clamp(selectedPage + 1, pageCount))}
          disabled={selectedPage >= pageCount}
          aria-label="Página siguiente"
        >
          <ChevronRight size={17} />
        </button>
      </footer>
    </aside>
  );
}
